import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../primitives/Button.js';
import { Input } from '../../primitives/Input.js';
import { Stack } from '../../primitives/Stack.js';
import { describedBy, FieldHost } from '../FieldHost.js';
const SIGNATURE_WIDTH = 480;
const SIGNATURE_HEIGHT = 160;
function strokesToCanvas(canvas, strokes) {
    const ctx = canvas.getContext('2d');
    if (ctx === null)
        return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';
    for (const stroke of strokes) {
        if (stroke.length === 0)
            continue;
        ctx.beginPath();
        const first = stroke[0];
        if (first === undefined)
            continue;
        ctx.moveTo(first.x, first.y);
        for (let i = 1; i < stroke.length; i += 1) {
            const p = stroke[i];
            if (p === undefined)
                continue;
            ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
    }
}
function canvasToPng(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob !== null) {
                resolve(blob);
                return;
            }
            try {
                const dataUrl = canvas.toDataURL('image/png');
                const comma = dataUrl.indexOf(',');
                const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : '';
                const bin = atob(base64);
                const bytes = new Uint8Array(bin.length);
                for (let i = 0; i < bin.length; i += 1)
                    bytes[i] = bin.charCodeAt(i);
                resolve(new Blob([bytes], { type: 'image/png' }));
            }
            catch (err) {
                reject(err instanceof Error ? err : new Error('failed to encode signature'));
            }
        }, 'image/png');
    });
}
function pointerToCanvas(canvas, clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width === 0 ? 1 : canvas.width / rect.width;
    const scaleY = rect.height === 0 ? 1 : canvas.height / rect.height;
    return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
    };
}
function renderTypedNameOnCanvas(canvas, name) {
    const ctx = canvas.getContext('2d');
    if (ctx === null)
        return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#111';
    ctx.textBaseline = 'middle';
    let size = 48;
    ctx.font = `${String(size)}px "Brush Script MT", "Lucida Handwriting", cursive`;
    while (ctx.measureText(name).width > canvas.width - 24 && size > 16) {
        size -= 2;
        ctx.font = `${String(size)}px "Brush Script MT", "Lucida Handwriting", cursive`;
    }
    ctx.fillText(name, 12, canvas.height / 2);
}
export function Signature(props) {
    const { item, errors, disabled, onChange } = props;
    const id = useId();
    const typedNameId = useId();
    const canvasRef = useRef(null);
    const [strokes, setStrokes] = useState([]);
    // Redo stack — when the patient hits Undo we push the popped stroke
    // here so a subsequent Redo restores it. Any new stroke wipes the
    // redo stack, matching the conventional editor model.
    const [redoStack, setRedoStack] = useState([]);
    const [typedName, setTypedName] = useState('');
    const drawingRef = useRef(false);
    useEffect(() => {
        const c = canvasRef.current;
        if (c === null)
            return;
        strokesToCanvas(c, strokes);
    }, [strokes]);
    const startStroke = useCallback((x, y) => {
        setStrokes((prev) => [...prev, [{ x, y }]]);
        setRedoStack([]);
        drawingRef.current = true;
    }, []);
    const extendStroke = useCallback((x, y) => {
        if (!drawingRef.current)
            return;
        setStrokes((prev) => {
            if (prev.length === 0)
                return prev;
            const lastIdx = prev.length - 1;
            const next = prev.slice(0, lastIdx);
            const last = prev[lastIdx];
            if (last === undefined)
                return prev;
            next.push([...last, { x, y }]);
            return next;
        });
    }, []);
    // Ref to the latest commit function, so `endStroke` can fire
    // auto-commit without depending on `save` (which is declared below
    // and itself depends on `strokes` / `typedName`).
    const saveRef = useRef(() => Promise.resolve());
    const endStroke = useCallback(() => {
        if (!drawingRef.current)
            return;
        drawingRef.current = false;
        // Auto-commit the carrier on pointer-up so the patient doesn't
        // have to remember to click an explicit Save button (bead 70z).
        void saveRef.current();
    }, []);
    const undo = useCallback(() => {
        setStrokes((prev) => {
            if (prev.length === 0)
                return prev;
            const last = prev[prev.length - 1];
            const next = prev.slice(0, -1);
            if (last !== undefined)
                setRedoStack((r) => [...r, last]);
            return next;
        });
        onChange(undefined);
    }, [onChange]);
    const redo = useCallback(() => {
        setRedoStack((prev) => {
            if (prev.length === 0)
                return prev;
            const last = prev[prev.length - 1];
            const next = prev.slice(0, -1);
            if (last !== undefined)
                setStrokes((s) => [...s, last]);
            return next;
        });
        onChange(undefined);
    }, [onChange]);
    const clear = useCallback(() => {
        setStrokes([]);
        setRedoStack([]);
        setTypedName('');
        onChange(undefined);
    }, [onChange]);
    const save = useCallback(async () => {
        const c = canvasRef.current;
        if (c === null)
            return;
        const trimmedName = typedName.trim();
        if (strokes.length === 0 && trimmedName !== '') {
            renderTypedNameOnCanvas(c, trimmedName);
        }
        else {
            strokesToCanvas(c, strokes);
        }
        const blob = await canvasToPng(c);
        const attachment = {
            contentType: 'image/png',
            size: blob.size,
            _blob: blob,
            ...(trimmedName !== '' ? { title: trimmedName } : {}),
        };
        onChange(attachment);
    }, [strokes, typedName, onChange]);
    // Keep saveRef pointed at the latest closure so endStroke (and the
    // typed-name auto-commit effect below) always commit current state.
    useEffect(() => {
        saveRef.current = save;
    }, [save]);
    // Auto-commit on typed-name changes too. Empty input clears the
    // carrier instead of committing an empty signature; non-empty fires
    // the same `save()` path the drawing flow uses. Guard against
    // re-firing during an active stroke (which would race with endStroke).
    useEffect(() => {
        if (drawingRef.current)
            return;
        const trimmed = typedName.trim();
        if (strokes.length === 0 && trimmed === '')
            return;
        void save();
        // `save` is intentionally omitted from deps — it changes on every
        // strokes/typedName tick, which would cause double-commits. The
        // typedName dep is what we actually want to react to; strokes
        // changes route through endStroke instead. (No exhaustive-deps
        // lint configured at the workspace level, so no disable directive
        // needed.)
    }, [typedName]);
    const hasContent = strokes.length > 0 || typedName.trim() !== '';
    const canUndo = strokes.length > 0;
    const canRedo = redoStack.length > 0;
    return (_jsx(FieldHost, { item: item, errors: errors, controlId: id, children: (ctx) => (_jsxs(Stack, { gap: 8, "data-signature-field": "true", children: [_jsxs("div", { className: "signature-pad-wrap", children: [_jsx("canvas", { id: id, ref: canvasRef, width: SIGNATURE_WIDTH, height: SIGNATURE_HEIGHT, "data-signature-canvas": "true", className: "signature-canvas", tabIndex: 0, role: "img", "aria-label": item.text !== undefined ? `Signature pad for ${item.text}` : 'Signature pad', "aria-invalid": ctx.hasError || undefined, "aria-describedby": describedBy(ctx, `${typedNameId}-help`), onPointerDown: (e) => {
                                if (disabled === true)
                                    return;
                                const c = canvasRef.current;
                                if (c === null)
                                    return;
                                c.setPointerCapture(e.pointerId);
                                const p = pointerToCanvas(c, e.clientX, e.clientY);
                                startStroke(p.x, p.y);
                            }, onPointerMove: (e) => {
                                if (disabled === true || !drawingRef.current)
                                    return;
                                const c = canvasRef.current;
                                if (c === null)
                                    return;
                                const p = pointerToCanvas(c, e.clientX, e.clientY);
                                extendStroke(p.x, p.y);
                            }, onPointerUp: endStroke, onPointerCancel: endStroke, onPointerLeave: endStroke }), strokes.length === 0 && typedName === '' ? (_jsx("span", { className: "signature-placeholder", "aria-hidden": "true", children: "Sign here" })) : null] }), _jsxs(Stack, { direction: "row", gap: 8, "data-signature-toolbar": "true", style: { flexWrap: 'wrap' }, children: [_jsx(Button, { type: "button", variant: "ghost", disabled: disabled === true || !canUndo, onClick: undo, children: '↶ Undo' }), _jsx(Button, { type: "button", variant: "ghost", disabled: disabled === true || !canRedo, onClick: redo, children: '↷ Redo' }), _jsx(Button, { type: "button", variant: "ghost", disabled: disabled === true || !hasContent, onClick: clear, children: 'Clear' })] }), _jsxs(Stack, { gap: 4, "data-signature-typed-affidavit": "true", children: [_jsx("label", { htmlFor: typedNameId, children: 'Or type your full name to sign' }), _jsx("p", { id: `${typedNameId}-help`, className: "muted", style: { margin: 0 }, children: 'If you cannot draw a signature, type your full legal name. Typing your name is a legally binding electronic signature.' }), _jsx(Input, { id: typedNameId, type: "text", value: typedName, disabled: disabled === true, autoComplete: "name", onChange: (e) => {
                                setTypedName(e.currentTarget.value);
                            } })] })] })) }));
}
//# sourceMappingURL=Signature.js.map